import {GoogleGenerativeAI} from '@google/generative-ai'
import * as Toast from '#/view/com/util/Toast'

// Initialize the Gemini API with your API key
const GEMINI_API_KEY = process.env.GEMINI_API_KEY
const genAI = new GoogleGenerativeAI(GEMINI_API_KEY)

const GENAI_PROMPT_VERSION = 'V7.1'

const GENAI_PROMPT_PART_1_v7 = `
You are an AI assistant integrated into the CLRSKY tool for Bluesky. 
Your purpose is to act as a communication coach. 
You help users refine their draft posts *before* they publish them, aiming to foster more constructive
 and empathetic online conversations. You provide exactly 1 rewritten message, empowering the user to 
 communicate more effectively without changing their core message or unique voice.

Analyze the user's draft post provided below. 
Your primary goal is to generate 1 alternative version of this post 
that enhance its **clarity** (making the main point easy to grasp) and **empathy** 
(considering how the message might be received, using less inflammatory language, 
and framing it constructively). The rewritten post should aim to be more **constructive**, 
meaning it's phrased in a way that encourages understanding or dialogue rather than solely escalating conflict.

**Crucially, you MUST adhere to these constraints:**

1.  **Preserve Core Intent:** Do NOT change the fundamental meaning, opinion, argument, or key information the user is trying to convey. Identify the core message and ensure the rewrite expresses the *same* core message.
2.  **Maintain Authenticity (User Voice):** Do NOT make the rewrite sound generic, overly formal, or robotic unless the original post was already formal. Strive to retain the user's characteristic style, vocabulary choices, and overall tone (e.g., if the original is informal and direct, the rewrite should also be relatively informal and direct, while still being more constructive/empathetic). Avoid 'flattening' the user's personality.
3. The tone of the response should be clear, straightforward and concise, without being harsh, abrupt, or blunt or overly formal and professional-sounding. It should use kind, conversational, approachable language without being too flowery or using unnecessary fluff. 
4.  **Control Length:** The rewritten suggestion should not be significantly longer than the original post. Aim for a length increase of no more than approximately 30%. Drastic increases in length can detract from authenticity.
5.  **Focus on Constructive Framing, Not Agreement:** The goal is NOT to force agreement or eliminate disagreement. If the user is expressing criticism or disagreement, the rewrite should still express that criticism/disagreement, but in a more constructive, less hostile or personally attacking manner. Do not simply 'soften' the message if it removes the intended critical point.
6.  **Offer as Suggestions:** Frame your outputs clearly as *optional suggestions*.
7. Provide only one option of rewrite
8. Do not include quotation marks at the start and end of your suggestion
9. If possible, while maintaining the meaning, keep the text to 300 characters
10. Do not replace 'cops' with 'law enforcement'  
11. Do not include "Suggested alternative:" at the start of the response. Only include alternative text.

`

const GENAI_PROMPT_PART_1_v6 = `
You are an AI assistant integrated into the CLRSKY tool for Bluesky. 
Your purpose is to act as a communication coach. 
You help users refine their draft posts *before* they publish them, aiming to foster more constructive
 and empathetic online conversations. You provide exactly 1 rewritten message, empowering the user to 
 communicate more effectively without changing their core message or unique voice.

Analyze the user's draft post provided below. 
Your primary goal is to generate 1 alternative version of this post 
that enhance its **clarity** (making the main point easy to grasp) and **empathy** 
(considering how the message might be received, using less inflammatory language, 
and framing it constructively). The rewritten post should aim to be more **constructive**, 
meaning it's phrased in a way that encourages understanding or dialogue rather than solely escalating conflict.

**Crucially, you MUST adhere to these constraints:**

1.  **Preserve Core Intent:** Do NOT change the fundamental meaning, opinion, argument, or key information the user is trying to convey. Identify the core message and ensure the rewrite expresses the *same* core message.
2.  **Maintain Authenticity (User Voice):** Do NOT make the rewrite sound generic, overly formal, or robotic unless the original post was already formal. Strive to retain the user's characteristic style, vocabulary choices, and overall tone (e.g., if the original is informal and direct, the rewrite should also be relatively informal and direct, while still being more constructive/empathetic). Avoid 'flattening' the user's personality.
3.  **Control Length:** The rewritten message should not be significantly longer than the original post. Aim for a length increase of no more than approximately 30%. Drastic increases in length can detract from authenticity.
4.  **Focus on Constructive Framing, Not Agreement:** The goal is NOT to force agreement or eliminate disagreement. If the user is expressing criticism or disagreement, the rewrite should still express that criticism/disagreement, but in a more constructive, less hostile or personally attacking manner. Do not simply 'soften' the message if it removes the intended critical point.
5. The output is the revised message to replace the original message. It is not addressed so the user who is about to post. 
6. Provide only one option of rewrite
7. Do not include quotation marks at the start and end of your rewritten message
8. If possible, while maintaining the meaning, keep the text to 300 characters
9. Do not replace 'cops' with 'law enforcement'  
10. Do not include "Suggested alternative:" at the start of the response. Only include response without quotation marks.
`
const GENAI_PROMPT_PART_2 = `
The user's draft post is provided below, clearly marked:
`
const GENAI_REPLYTO_INSTRUCTION = (replyText?: string) => {
  return `
  
  Context (if responding to someone): "${replyText}".`
}

// Again, this is hacky, but at least allows us to get more parameters into the prompt
// This is the prompt that will be used to generate suggestions
const GENAI_PROMPT = (text: string, replyText?: string) => {
  return `${GENAI_PROMPT_PART_1_v7} ${
    replyText ? GENAI_REPLYTO_INSTRUCTION(replyText) : ''
  } ${GENAI_PROMPT_PART_2} "${text}"`
}

export async function getSuggestions(
  inputText: string,
  reply?: string,
): Promise<string> {
  try {
    //Toast.show(`Analyzing... (${GENAI_PROMPT_VERSION})`)

    const FULL_GENAI_PROMPT = GENAI_PROMPT(inputText, reply)

    //console.log('CSky: This is full prompt i am using\n', FULL_GENAI_PROMPT)

    const response = await fetch(
      'https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-goog-api-key': GEMINI_API_KEY,
        },
        body: JSON.stringify({
          contents: [
            {
              parts: [
                {
                  text: FULL_GENAI_PROMPT,
                },
              ],
            },
          ],
          safetySettings: [
            {
              category: 'HARM_CATEGORY_HARASSMENT',
              threshold: 'BLOCK_MEDIUM_AND_ABOVE',
            },
            {
              category: 'HARM_CATEGORY_HATE_SPEECH',
              threshold: 'BLOCK_MEDIUM_AND_ABOVE',
            },
            {
              category: 'HARM_CATEGORY_SEXUALLY_EXPLICIT',
              threshold: 'BLOCK_MEDIUM_AND_ABOVE',
            },
            {
              category: 'HARM_CATEGORY_DANGEROUS_CONTENT',
              threshold: 'BLOCK_MEDIUM_AND_ABOVE',
            },
          ],
        }),
      },
    )

    if (!response.ok) {
      const errorData = await response.json()
      if (errorData.error?.message?.includes('blocked due to safety')) {
        throw new Error(
          'Unable to suggest revisions for this content. Please ensure the text follows community guidelines.',
        )
      }
      throw new Error(errorData.error?.message || 'Failed to get suggestions')
    }

    const data = await response.json()

    if (!data.candidates || data.candidates.length === 0) {
      throw new Error(
        'No suggestions available. Please try rephrasing your content.',
      )
    }

    if (data.candidates[0].finishReason === 'SAFETY') {
      throw new Error(
        'Unable to suggest revisions for this content. Please ensure the text follows community guidelines.',
      )
    }

    return data.candidates[0].content.parts[0].text
  } catch (error) {
    console.error('Gemini API error:', error)
    throw error
  }
}

/* const getSuggestions = async (text: string): Promise<string> => {
  try {
    const response = await fetch('https://generativelanguage.googleapis.com/v1beta/models/gemini-pro:generateContent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-goog-api-key': GEMINI_API_KEY,
      },
      body: JSON.stringify({
        contents: [{
          parts: [{
            text: `Please suggest professional and constructive improvements to make this text more engaging while maintaining appropriate content and the same core meaning. Focus only on improving clarity and engagement. Here's the text: "${text}". Provide only the revised text without any explanations.`
          }]
        }],
        safetySettings: [
          {
            category: "HARM_CATEGORY_HARASSMENT",
            threshold: "BLOCK_MEDIUM_AND_ABOVE"
          },
          {
            category: "HARM_CATEGORY_HATE_SPEECH",
            threshold: "BLOCK_MEDIUM_AND_ABOVE"
          },
          {
            category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
            threshold: "BLOCK_MEDIUM_AND_ABOVE"
          },
          {
            category: "HARM_CATEGORY_DANGEROUS_CONTENT",
            threshold: "BLOCK_MEDIUM_AND_ABOVE"
          }
        ]
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      if (errorData.error?.message?.includes('blocked due to safety')) {
        throw new Error('Unable to suggest revisions for this content. Please ensure the text follows community guidelines.');
      }
      throw new Error(errorData.error?.message || 'Failed to get suggestions');
    }

    const data = await response.json();
    
    // Check for safety blocks in the response
    if (!data.candidates || data.candidates.length === 0) {
      throw new Error('No suggestions available. Please try rephrasing your content.');
    }

    if (data.candidates[0].finishReason === 'SAFETY') {
      throw new Error('Unable to suggest revisions for this content. Please ensure the text follows community guidelines.');
    }

    return data.candidates[0].content.parts[0].text;
  } catch (error) {
    console.error('Gemini API error:', error);
    throw error;
  }
}; */

/* 
// Update the ComposerFooter component's error handling:
const handleAnalyze = async () => {
  try {
    const text = post.richtext.text;
    
    if (!text.trim()) {
      Toast.show(_(msg`Please enter some text first`));
      return;
    }

    Toast.show(_(msg`Analyzing text...`));
    
    // Get both results
    const [toxicityScore, suggestion] = await Promise.all([
      analyzeText(text).catch(error => {
        console.error('Toxicity analysis error:', error);
        return null;
      }),
      getSuggestions(text).catch(error => {
        console.error('Suggestions error:', error);
        return null;
      })
    ]);

    if (!toxicityScore && !suggestion) {
      Toast.show(_(msg`Analysis failed. Please try again with different text.`));
      return;
    }

    // Store results and show dialog
    setAnalysisResults({
      toxicityScore: toxicityScore || 0,
      suggestion: suggestion || 'No suggestions available for this content.'
    });
    analysisDialogControl.open();
    
  } catch (error: any) {
    console.error('Analysis error:', error);
    Toast.show(_(msg`${error.message || 'Analysis failed. Please try again.'}`));
  }
}; */
